import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';

// Storage key for session (must match AuthService)
const STORAGE_KEY = 'ams_session';

/**
 * HTTP interceptor that handles API errors globally.
 * - 401 Unauthorized: Clears session and redirects to login
 * - 403 Forbidden: Redirects to dashboard with access denied
 * - Other errors: Passes through for component-level handling
 *
 * Note: We clear localStorage directly to avoid circular dependency with AuthService.
 */
export const errorInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
) => {
  const router = inject(Router);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        // Don't clear session for logout requests — they are fire-and-forget and may
        // return 401 (expired token) after a new user has already logged in.
        if (!req.url.includes('/auth/logout')) {
          // Guard against stale 401s from requests made before a logout/re-login.
          // If the request's token no longer matches the current session token, this
          // is a response to a stale request — ignore it to avoid disrupting the
          // new session that has already been established.
          const storedRaw = localStorage.getItem(STORAGE_KEY);
          const currentToken = storedRaw ? JSON.parse(storedRaw).accessToken : null;
          const requestToken = req.headers.get('Authorization')?.replace('Bearer ', '');

          if (!currentToken || requestToken === currentToken) {
            console.warn('Unauthorized request, clearing session');
            localStorage.removeItem(STORAGE_KEY);
            // Carry the page they were on into the login round-trip. Routes are
            // guarded (authGuard supplies returnUrl before the shell renders), so
            // this is the backstop for a session that dies mid-visit — e.g. a token
            // expiring while the user sits on a deep-linked page. The launcher
            // sanitizes the value and drops /launcher and /auth/callback, so passing
            // the current URL blindly is safe.
            router.navigate(['/launcher'], { queryParams: { returnUrl: router.url } });
          }
        }
        return throwError(() => error);
      }

      if (error.status === 403) {
        // Access denied — log and let the caller/guard handle navigation
        console.warn('Forbidden request:', error.url);
        return throwError(() => error);
      }

      if (error.status === 0) {
        // Network error or CORS issue
        console.error('Network error or API unreachable:', error.message);
      }

      // Skip logging for 404s — callers handle them locally (e.g. optional endpoints)
      if (error.status === 404) {
        return throwError(() => error);
      }

      // Log error for debugging
      console.error('API Error:', {
        status: error.status,
        statusText: error.statusText,
        url: error.url,
        message: error.error?.error || error.message,
      });

      // Pass the error through for component-level handling
      return throwError(() => error);
    })
  );
};
